// 入力小説そのものを正として、生成言語・原作URL・開始画面サブタイトルを決める共通規則。
// LLMやUIの表示言語に依存させず、解析・編集保存・スキャフォールドで同じ判定を使う。
import { pipelineConfig, str } from './story-shared.mjs';

const codePoints = (value) => Array.from(String(value ?? ''));

export function codePointLength(value) {
    return codePoints(value).length;
}

function sourcePolicy() {
    return pipelineConfig().sourceDocumentPolicy ?? {};
}

/** 日本語は仮名の実在で判定し、それ以外（英語を含む）はすべて英語生成へ倒す。 */
export function detectNovelGenerationLanguage(text) {
    const normalized = String(text ?? '').normalize('NFKC');
    const compact = normalized.replace(/\s+/gu, '');
    const kanaCount = (normalized.match(/[\u3040-\u30ff]/gu) ?? []).length;
    const cfg = sourcePolicy().japaneseDetection ?? {};
    const minKana = Number.isFinite(Number(cfg.minKanaCharacters)) ? Number(cfg.minKanaCharacters) : 10;
    const minRatio = Number.isFinite(Number(cfg.minKanaRatio)) ? Number(cfg.minKanaRatio) : 0.01;
    const kanaRatio = compact.length > 0 ? kanaCount / codePointLength(compact) : 0;
    return kanaCount >= minKana && kanaRatio >= minRatio ? 'ja' : 'en';
}

function domainMatches(hostname, allowedDomain) {
    const host = String(hostname ?? '').toLowerCase().replace(/^www\./, '');
    const domain = String(allowedDomain ?? '').toLowerCase().replace(/^www\./, '');
    return host === domain || host.endsWith(`.${domain}`);
}

function stripUrlPunctuation(value) {
    return String(value ?? '').replace(/[\])}>）】』」〉》。、，,.;:!?！？]+$/gu, '');
}

export function normalizeOriginalNovelUrl(value, generationLanguage = 'ja') {
    const raw = stripUrlPunctuation(str(value));
    if (!raw) return null;
    let parsed;
    try {
        parsed = new URL(raw);
    } catch {
        return null;
    }
    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
    const language = generationLanguage === 'ja' ? 'ja' : 'en';
    const allowed = sourcePolicy().publicationDomains?.[language] ?? [];
    if (!allowed.some((domain) => domainMatches(parsed.hostname, domain))) return null;
    parsed.hash = '';
    return parsed.href;
}

function originalUrlKey(value) {
    try {
        const parsed = new URL(value);
        const pathname = parsed.pathname.replace(/\/+$/u, '') || '/';
        return `${parsed.hostname.toLowerCase().replace(/^www\./, '')}${pathname}`;
    } catch {
        return '';
    }
}

/** 原文に文字どおり存在する、対応小説公開サイトのURLだけを登場順で返す。 */
export function extractOriginalNovelUrls(text, generationLanguage = detectNovelGenerationLanguage(text)) {
    const found = String(text ?? '').match(/https?:\/\/[^\s<>"'`]+/giu) ?? [];
    const urls = [];
    const seen = new Set();
    for (const candidate of found) {
        const normalized = normalizeOriginalNovelUrl(candidate, generationLanguage);
        if (!normalized) continue;
        const key = originalUrlKey(normalized);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        urls.push(normalized);
    }
    return urls;
}

/** 編集値は原文内URLと同じ作品パスの場合のみ許可する。空欄は明示的な非表示指定。 */
export function resolveDocumentOriginalUrl(requestedValue, documentUrls, generationLanguage) {
    if (requestedValue == null || str(requestedValue) === '') return null;
    const requested = normalizeOriginalNovelUrl(requestedValue, generationLanguage);
    if (!requested) return null;
    const key = originalUrlKey(requested);
    return documentUrls.find((candidate) => originalUrlKey(candidate) === key) ?? null;
}

export function inspectNovelSource(text) {
    const generationLanguage = detectNovelGenerationLanguage(text);
    const originalUrls = extractOriginalNovelUrls(text, generationLanguage);
    return {
        sourceLanguage: generationLanguage,
        generationLanguage,
        originalUrls,
        originalUrl: originalUrls[0] ?? null,
    };
}

function normalizeSubtitleText(value) {
    return str(value).replace(/\s+/gu, ' ').trim();
}

/**
 * LLMには要約を指示しつつ、最終防衛線として必ず設定上限以下へ正規化する。
 * 長文が複数文なら、導入説明より物語のフックになりやすい末尾の短い文を優先する。
 */
export function normalizeStorySubtitle(value, fallback = '') {
    const cfg = sourcePolicy();
    const max = Math.max(1, Number(cfg.subtitleMaxCharacters) || 40);
    const minCandidate = Math.max(1, Number(cfg.subtitleCandidateMinCharacters) || 12);
    const source = normalizeSubtitleText(value) || normalizeSubtitleText(fallback);
    if (codePointLength(source) <= max) return source;

    const sentences = source.match(/[^。！？!?]+[。！？!?]?/gu) ?? [];
    const fittingSentence = sentences
        .map(normalizeSubtitleText)
        .filter((candidate) => codePointLength(candidate) >= minCandidate && codePointLength(candidate) <= max)
        .at(-1);
    if (fittingSentence) return fittingSentence;

    const clauses = source.split(/[、，,；;：:]/gu)
        .map(normalizeSubtitleText)
        .filter((candidate) => codePointLength(candidate) >= minCandidate && codePointLength(candidate) <= max);
    if (clauses.length > 0) return clauses.at(-1);

    if (max === 1) return '…';
    const clipped = codePoints(source).slice(0, max - 1).join('').replace(/[\s、，,；;：:]+$/gu, '');
    return `${clipped}…`;
}

export function generationLanguageName(generationLanguage = 'ja') {
    return generationLanguage === 'ja' ? '日本語' : '英語';
}

export function generationLanguageInstruction(generationLanguage = 'ja') {
    return generationLanguage === 'ja'
        ? '入力小説は日本語として判定した。ツールやゲーム実行環境の表示言語に関係なく、ゲーム本文・会話・通知・説明は日本語を基準に生成する。'
        : '入力小説は英語、または日本語・英語以外として判定した。日本語以外は英語として扱い、ツールやゲーム実行環境の表示言語に関係なく、ゲーム本文・会話・通知・説明は英語を基準に生成する。';
}

export function requiredTranslationPolicy(generationLanguage = 'ja') {
    return generationLanguage === 'ja'
        ? '入力小説が日本語のため、実行環境の表示言語に関係なく、ゲーム本文・セリフ・通知は日本語を基準に生成する。'
        : '入力小説が英語、または日本語・英語以外のため、日本語以外は英語として扱い、実行環境の表示言語に関係なく、ゲーム本文・セリフ・通知は英語を基準に生成する。';
}

export function withRequiredTranslationPolicy(value, generationLanguage) {
    const required = requiredTranslationPolicy(generationLanguage);
    const detail = str(value);
    return detail && !detail.includes(required) ? `${required} ${detail}` : (detail || required);
}

export function localizedForGeneration(value, generationLanguage = 'ja', fallback = '') {
    if (typeof value === 'string') return str(value) || str(fallback);
    const primary = generationLanguage === 'ja' ? value?.ja : value?.en;
    const secondary = generationLanguage === 'ja' ? value?.en : value?.ja;
    return str(primary) || str(secondary) || str(fallback);
}
